<template>
  <div class="sidebar" :class="{ collapsed: isCollapsed }">
    <div class="sidebar-header">
      <button class="button toggle-btn" @click="toggleSidebar" aria-label="Toggle Sidebar">
        <span class="icon">
          <i class="fas" :class="isCollapsed ? 'fa-arrow-right' : 'fa-arrow-left'"></i>
        </span>
      </button>
    </div>

    <nav class="sidebar-nav">
      <a
        v-for="item in menuItems"
        :key="item.id"
        :href="item.link"
        class="nav-item"
        :class="{ active: activeItem === item.id }"
        @click.prevent="selectItem(item.id)"
      >
        <span class="nav-icon">
          <i :class="item.icon"></i>
        </span>
        <span class="nav-text" v-if="!isCollapsed">{{ item.label }}</span>
      </a>
    </nav>

    <!-- Logout Button -->
    <div class="sidebar-footer">
      <a
        href="#"
        class="nav-item logout-item"
        @click.prevent="handleLogout"
      >
        <span class="nav-icon">
          <i class="fas fa-sign-out-alt"></i>
        </span>
        <span class="nav-text" v-if="!isCollapsed">Logout</span>
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: "Sidebar",
  props: {
    menuItems: {
      type: Array,
      required: true,
      validator: (items) => {
        return items.every(
          (item) =>
            item.id &&
            item.label &&
            item.icon &&
            item.link
        );
      },
    },
  },
  data() {
    return {
      isCollapsed: false,
      activeItem: "applications",
    };
  },
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit("toggle", this.isCollapsed);
    },
    selectItem(itemId) {
      this.activeItem = itemId;
      this.$emit("select", itemId);
    },
    async handleLogout() {
      console.log("Logging out...");
      try {
        // Sign out via Azure B2C
        await this.$store.dispatch('auth/signOut');
        // The auth store will handle redirect to login
      } catch (error) {
        console.error('Logout error:', error);
        // Fallback: redirect to login anyway
        this.$router.push('/login');
      }
    },
  },
};
</script>

<style scoped>
.sidebar {
  position: fixed;
  left: 0;
  top: 70px; /* Start below the header */
  height: calc(100vh - 70px); /* Adjust height to account for header */
  background: #0f4d90; /* Dark Ben Franklin Blue - Philly Style Guide */
  color: #ffffff;
  width: 260px;
  transition: width 0.3s ease;
  z-index: 1000;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: visible; /* Allow toggle button to remain visible */
}

.sidebar.collapsed {
  width: 25px;
  overflow: visible; /* Allow toggle button to remain visible */
}

.sidebar-header {
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: none;
  min-height: 0;
  height: 0;
  position: relative;
}

.toggle-btn {
  position: fixed; /* Fixed positioning to stay above content */
  left: 248px; /* Position at the edge of the open sidebar */
  top: calc(70px + 2rem); /* Account for header height + offset */
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #444444 !important;
  flex-shrink: 0;
  border: none !important;
  background: #ffffff !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.2s ease;
  outline: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  z-index: 1002; /* Higher than dashboard content */
}

.sidebar.collapsed .toggle-btn {
  left: 12px; /* When collapsed, position near left edge */
}

.toggle-btn .icon {
  margin: 0 !important;
  font-size: 0.7rem;
}

.toggle-btn .icon i {
  color: #444444 !important;
  font-size: 0.7rem;
}

.toggle-btn:hover,
.toggle-btn:focus {
  background: #f0f0f0 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  border: none !important;
  outline: none !important;
  transform: scale(1.05);
}

.toggle-btn:hover .icon,
.toggle-btn:hover .icon i {
  color: #2176d2 !important; /* Ben Franklin Blue on hover */
}

.toggle-btn:focus .icon,
.toggle-btn:focus .icon i {
  color: #444444 !important;
}

.toggle-btn:active {
  background: #e0e0e0 !important;
  border: none !important;
  outline: none !important;
  transform: scale(0.98);
}


.sidebar-nav {
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
}

.sidebar.collapsed .sidebar-nav {
  opacity: 0;
  visibility: hidden;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  color: #ffffff;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border-left: 3px solid transparent;
  font-family: 'Open Sans', sans-serif;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-left-color: #2176d2; /* Ben Franklin Blue - Philly Style Guide */
}

.nav-item.active {
  background: rgba(33, 118, 210, 0.2); /* Ben Franklin Blue with opacity */
  border-left-color: #2176d2; /* Ben Franklin Blue - Philly Style Guide */
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  font-size: 1.25rem;
  min-width: 24px;
}

.nav-text {
  margin-left: 1rem;
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.sidebar.collapsed .nav-text {
  opacity: 0;
  width: 10px;
  overflow: hidden;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  padding: 1rem;
}

/* Sidebar Footer */
.sidebar-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.5rem 0;
}

.sidebar.collapsed .sidebar-footer {
  opacity: 0;
  visibility: hidden;
}

.logout-item {
  border-left: 3px solid transparent;
}

.logout-item:hover {
  background: rgba(204, 48, 0, 0.2); /* Love Park Red with opacity - Philly Style Guide */
  border-left-color: #cc3000; /* Love Park Red - Philly Style Guide */
}

/* Scrollbar styling for sidebar */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Responsive */
@media (max-width: 768px) {
  .sidebar {
    width: 0;
    overflow: visible;
    top: 60px; /* Adjust for smaller header on mobile */
    height: calc(100vh - 60px);
  }

  .sidebar.collapsed {
    width: 260px;
  }

  .toggle-btn {
    top: calc(60px + 2rem); /* Adjust for smaller header on mobile */
  }

  .sidebar.collapsed .toggle-btn {
    left: 248px;
  }
}
</style>
